import { ToastInput, ToastRecord, ToastTone } from '../../../types/DesignSystem/Toast';

/**
 * The toast store — a module-level list with a subscribe/snapshot pair for
 * `useSyncExternalStore`.
 *
 * **Why a module store and not React context.** A toast is raised from wherever the
 * thing happened, and in this app that is very often *not* a component: an axios
 * interceptor that catches a 409, the `CanvasAutoSave` effect bridge, a promote
 * handler. A context-only API forces every one of those call sites to be inside the
 * tree and to thread a hook down to it. `toast('Saved')` works from anywhere,
 * including plain modules, which is the whole point.
 *
 * The trade is that the store is global state, so tests must reset it — hence
 * `resetToasts()`, which exists for that and is not part of the public API.
 */

type Listener = () => void;

const listeners = new Set<Listener>();
let toasts: ToastRecord[] = [];
let seq = 0;
let autoId = 0;

/**
 * At most this many are on screen. A fourth push drops the oldest toast that would
 * have left on its own.
 *
 * Stated rather than silent: a cap that quietly discards is indistinguishable from
 * a toast that never fired, which is the sort of thing that costs an afternoon.
 * Three is the point where the stack still reads as a list rather than a wall.
 *
 * § 342 (2026-09-29) — the cap no longer deletes a STICKY toast (`duration: null`,
 * which every `danger` is by default). It used to drop the oldest record whatever it
 * was, so an error raised before three newer toasts was deleted before anyone read
 * it, and this comment said so ("the first error can scroll off"). A sticky toast
 * waits for acknowledgement, so the stack exceeds the cap rather than lose one — see
 * `trimToCap`.
 */
export const MAX_VISIBLE = 3;

/**
 * § 342 — which records a full stack gives up, in order: first a toast that is
 * already leaving (dismissed, still animating out), then the oldest that would have
 * auto-dismissed anyway. A sticky toast is never given up, and neither is the toast
 * being pushed. When only sticky toasts remain over the cap, the stack stays taller
 * than `MAX_VISIBLE` until someone dismisses one.
 */
const trimToCap = (list: ToastRecord[], pushedId: string): ToastRecord[] => {
  const passes: Array<(t: ToastRecord) => boolean> = [
    (t) => !t.open,
    (t) => t.duration !== null,
  ];
  let next = list;
  for (const evictable of passes) {
    while (next.length > MAX_VISIBLE) {
      const at = next.findIndex((t) => t.id !== pushedId && evictable(t));
      if (at === -1) break;
      next = [...next.slice(0, at), ...next.slice(at + 1)];
    }
  }
  return next;
};

/**
 * How long each tone stays.
 *
 * `danger` is **sticky on purpose**. An auto-dismissing message is a time limit
 * under WCAG SC 2.2.1, and an error that vanishes before it is read is the case
 * that criterion exists for. The other tones auto-dismiss but **pause on hover and
 * on focus** (see `Toast.tsx`), which is the accepted mitigation. `warning` gets
 * longer than the rest because it usually carries something to act on.
 */
export const DEFAULT_DURATION: Record<ToastTone, number | null> = {
  neutral: 5000,
  info: 5000,
  success: 5000,
  warning: 8000,
  danger: null,
};

const emit = () => { listeners.forEach((l) => l()); };

export const subscribe = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

/**
 * Returns the SAME array reference until something changes. `useSyncExternalStore`
 * compares snapshots with `Object.is` and re-renders forever if handed a fresh
 * array each call, so every mutation below replaces `toasts` exactly once.
 */
export const getToasts = (): ToastRecord[] => toasts;

/** Raise a toast. Returns its id, which `dismiss` and a later replace can use. */
export function pushToast(input: ToastInput): string {
  const id = input.id ?? `toast-${++autoId}`;
  const tone: ToastTone = input.tone ?? 'neutral';
  /* § 407 — a toast with an ACTION stays until it is acted on or dismissed, unless the caller
     gives it a duration. On the tone's five seconds, "Undo" was gone before a keyboard user
     could reach a region rendered at the end of the page (WCAG 2.2.1: hover and focus pause the
     clock, but a user has to get there first). Polaris and Carbon keep action toasts the same way. */
  const duration = input.duration === undefined
    ? (input.action ? null : DEFAULT_DURATION[tone])
    : input.duration;
  const record: ToastRecord = { ...input, id, tone, duration, open: true, seq: ++seq };

  const at = toasts.findIndex((t) => t.id === id);
  /* A replace keeps its POSITION in the stack. Re-appending would make a
     "Saving…" → "Saved" swap jump the toast to the bottom of the column, which
     reads as a new event rather than the same one resolving. */
  let next = at >= 0
    ? toasts.map((t, i) => (i === at ? record : t))
    : [...toasts, record];

  if (next.length > MAX_VISIBLE) next = trimToCap(next, id);

  toasts = next;
  emit();
  return id;
}

/** Begin the exit. The record stays until `removeToast` runs after the animation. */
export function dismissToast(id: string): void {
  if (!toasts.some((t) => t.id === id && t.open)) return;
  toasts = toasts.map((t) => (t.id === id ? { ...t, open: false } : t));
  emit();
}

/** Drop the record. Called by `Toast` once its exit animation has finished. */
export function removeToast(id: string): void {
  if (!toasts.some((t) => t.id === id)) return;
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

/** Dismiss everything on screen — e.g. on route change. */
export function dismissAllToasts(): void {
  if (!toasts.some((t) => t.open)) return;
  toasts = toasts.map((t) => (t.open ? { ...t, open: false } : t));
  emit();
}

/** Test-only: clear the list and the counters so ids are deterministic per test. */
export function resetToasts(): void {
  toasts = [];
  seq = 0;
  autoId = 0;
  emit();
}
